<template>
    <div class="hob-cutout-view view" :class="{ incomplete: state.hobcutout != '1' }">
        <instructions titleKey="hobcutout" property="hobcutoutHelpContent" />

        <worktop-drawing :editableFields="[ 'hobcutoutLength', 'hobcutoutWidth', 'hobcutoutDistanceLeft', 'hobcutoutDistanceRight', 'hobcutoutDistanceFront' ]" />

        <div class="wrapper">
            <div class="section-content">
                <div class="col-3">
                    <h2 class="h2" v-html="ui.t('hobcutoutChoose')"></h2>

                    <label class="form-group col-10">
                        <div class="image-group">
                            <a href="#" class="list-group-item active" @click.prevent="hobcutoutPopupOpen = true">
                                <div class="container">
                                    <div class="top">
                                        <div class="image" v-if="hobcutout.image" :style="{ 'background-image': ui.getBackgroundImageUrl(hobcutout.image, 200)! }"></div>

                                        <h3>
                                            {{ hobcutout.name }}
                                        </h3>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </label>
                </div>

                <div class="col-3" v-if="state.hobcutout == '1'">
                    <h2 class="h2" v-html="ui.t('hobcutoutDimensions')"></h2>

                    <span class="input-tip" v-if="!hasSpaceAvailable" v-html="ui.t('hobcutoutNoSpace')"></span>

                    <number-input v-model="ui.hobcutoutLength"
                        resourceKey="hobcutoutLength"
                        :classes="inputClasses"
                        @blur="ui.clearFocusedField()"
                        @focus="ui.focusedFieldName = 'hobcutoutLength'"
                        :max="maxLength"
                        :min="32" />

                    <number-input v-model="ui.hobcutoutWidth"
                        resourceKey="depth"
                        :classes="inputClasses"
                        @blur="ui.clearFocusedField()"
                        @focus="ui.focusedFieldName = 'hobcutoutWidth'"
                        :max="ui.worktopWidth - (45 * 2)"
                        :min="32" />
                </div>

                <div v-if="hasDimensionsSet && hasSpaceAvailable" class="col-3">
                    <!-- <component :is="infoDisplayComponent" accessKey="hobcutoutPosition"></component> -->
                    <h2 class="h2" v-html="ui.t('hobcutoutDistance')"></h2>

                    <template v-if="state.hobcutoutDistanceMeasuredTo == 'edge'">
                        <number-input v-model="ui.hobcutoutDistanceLeft"
                            resourceKey="hobcutoutDistanceLeft"
                            :classes="inputClasses"
                            @blur="ui.clearFocusedField()"
                            @focus="ui.focusedFieldName = 'hobcutoutDistanceLeft'"
                            :max="ui.worktopLength! - ui.hobcutoutLength!  - 45"
                            :min="45" />

                        <number-input v-model="ui.hobcutoutDistanceRight"
                            resourceKey="hobcutoutDistanceRight"
                            :classes="inputClasses"
                            @blur="ui.clearFocusedField()"
                            @focus="ui.focusedFieldName = 'hobcutoutDistanceRight'"
                            :max="ui.worktopLength! - ui.hobcutoutLength!  - 45"
                            :min="45" />
                    </template>

                    <template v-if="state.hobcutoutDistanceMeasuredTo == 'center'">
                        <number-input v-model="ui.hobcutoutDistanceLeftCenter"
                            resourceKey="hobcutoutDistanceLeft"
                            :classes="inputClasses"
                            @blur="ui.clearFocusedField()"
                            @focus="ui.focusedFieldName = 'hobcutoutDistanceLeftCenter'"
                            :max="ui.worktopLength! - ui.hobcutoutLength! - 45 + ui.hobcutoutLength! / 2"
                            :min="45 + ui.hobcutoutLength! / 2" />

                        <number-input v-model="ui.hobcutoutDistanceRightCenter"
                            resourceKey="hobcutoutDistanceRight"
                            :classes="inputClasses"
                            @blur="ui.clearFocusedField()"
                            @focus="ui.focusedFieldName = 'hobcutoutDistanceRightCenter'"
                            :max="ui.worktopLength! - ui.hobcutoutLength! - 45 + ui.hobcutoutLength! / 2"
                            :min="45 + ui.hobcutoutLength! / 2" />
                    </template>

                    <number-input v-model="ui.hobcutoutDistanceFront"
                        resourceKey="hobcutoutDistanceFront"
                        :classes="inputClasses"
                        @blur="ui.clearFocusedField()"
                        @focus="ui.focusedFieldName = 'hobcutoutDistanceFront'"
                        :max="Math.min((ui.worktopWidth ?? 0) - (ui.hobcutoutWidth ?? 0) - 45, 99)"
                        :min="45" />
                </div>

                <div class="col-3" v-if="hasDimensionsSet && hasSpaceAvailable">
                    <h2 class="h2" v-html="ui.t('hobcutoutDistanceOrigin')"></h2>

                    <option-list v-model="state.hobcutoutDistanceMeasuredTo" :optionList="hobcutoutMeasurementTypes" />
                </div>
            </div>
        </div>

        <hobcutout-selection-popup v-model:visible="hobcutoutPopupOpen" />

        <div class="navigation">
            <div class="wrapper">
                <router-link :to="`/${state.productFamilyCode}/sink`" v-html="ui.t('previous')" />
                <router-link :to="ui.functionalAreaAvailable ? `/${state.productFamilyCode}/functional-area` : `/${state.productFamilyCode}/accessories`" @click.native="moveNext" :class="{ next: true,  disabled: !ui.hobcutoutSelectionsComplete }">
                    <span class="input-tip" v-if="moveNextPrevented" v-html="ui.t('selectionsIncomplete')"></span>
                    <span v-html="ui.t('next')"></span>
                </router-link>
            </div>
        </div>
    </div>
</template>

<script async setup lang="ts">
import { computed, ref } from 'vue';
import { useConfiguration, useState, useUI } from '../store';

const { configuration } = useConfiguration();
const { state } = useState();
const { ui } = useUI();

const hobcutoutPopupOpen = ref(false);
const moveNextPrevented = ref(false);

let moveNextTimer: number | null = null;

function moveNext() {
    moveNextPrevented.value = true;

    moveNextTimer = setTimeout(() => moveNextPrevented.value = false, 3000);
}

function created() {
    if (!state.hobcutout)
        hobcutoutPopupOpen.value = true;
}

const hobcutout = computed(() => {
    let hobcutout = configuration.models.hobcutouts.find(s => s.code == state.hobcutout);

    if (hobcutout == null) {
        let noHobCutout = configuration.models.hobcutouts.find(s => s.code == "0");

        return {
            name: null,
            code: null,
            handedness: null,
            description: null,
            image: noHobCutout!.image,
            embedMedia: null,
            link: null,
            drawing: null,
            minLength: null,
            maxLength: null,
            minHeight: null,
            maxHeight: null,
            fixedDistanceFront: null,
            step: null,
            visibleInVertex: true,
            sinkCount: 0,
            length: 0,
            width: 0,
            height: 0,
            rules: null,
            warnings: null
        }
    }

    return hobcutout;
});

const hobcutoutMeasurementTypes = computed(() => {
    return {
        title: null,
        options: [
            {
                value: "edge",
                text: ui.t("hobcutoutToEdge"),
                image: null,
                embedMedia: null,
                link: null,
                disabled: false,
                visible: true,
                warnings: [],
                messages: []
            },
            {
                value: "center",
                text: ui.t("hobcutoutToCenter"),
                image: null,
                embedMedia: null,
                link: null,
                disabled: false,
                visible: true,
                warnings: [],
                messages: []
            }
        ]
    }
});

const maxLength = computed(() => {
    let sinkSpace = ui.sink.length > 0 ? ui.sink.length + 100 : 0;

    return ui.worktopLength! - sinkSpace - (45 * 2);
});

const hasDimensionsSet = computed(() => {
    return state.hobcutout == '1' && ui.hobcutoutLength! > 0 && ui.hobcutoutWidth! > 0;
});

const hasSpaceAvailable = computed(() => {
    if (state.hobcutout == '0' || !ui.hobcutoutLength || !ui.hobcutoutWidth)
        return true;

    let availableSpaces = [
        (ui.sinkDistanceLeft! || ui.worktopLength!) - 45  - (ui.sinkDistanceLeft ? ui.hobcutoutMargins : 45),
        (ui.sinkDistanceRight! || ui.worktopLength!) - 45 - (ui.sinkDistanceRight ? ui.hobcutoutMargins : 45)
    ];

    var availableSpace = availableSpaces.find(s => s >= ui.hobcutoutLength!);

    return !!availableSpace && ui.hobcutoutWidth <= (ui.worktopWidth - (45 * 2));
});

const inputClasses = computed(() => {
    let classes = [ ];

    if (ui.hobcutoutHasPlacementError) {
        classes.push("invalid");
    }

    return classes;
});

await created();
</script>
